import { fetchStoriesCatalog } from './storiesFetcher';
import { getAutoplaysettings } from './autoplaysettings';

// Module-level cache so repeated calls during the same session skip the network.
let catalogCache = null;

async function getCatalog() {
  if (!catalogCache) {
    catalogCache = await fetchStoriesCatalog();
  }
  return catalogCache;
}

/**
 * Build the flat ordered sequence of all stories across all characters.
 *
 * Character order comes from settings.characterOrder so the user's preference
 * (set in Settings → Learner → Story Autoplay) is respected. Each story item
 * is shaped to match the format StoryPlayerPage reads from sessionStorage so
 * handleAutoNextStory can store it directly.
 *
 * @returns {Promise<Array<{storyId, character, characterLower, url, title, image}>>}
 */
export async function buildStorySequence() {
  const settings = getAutoplaysettings();
  const catalog = await getCatalog();
  const order = Array.isArray(settings.characterOrder)
    ? settings.characterOrder
    : ['vector', 'pebbles', 'milo'];

  const sequence = [];

  for (const charKey of order) {
    const lower = charKey.toLowerCase();
    const charGroup = catalog.find(
      (c) => String(c.title || '').toLowerCase() === lower,
    );
    if (!charGroup) continue;

    const items = Array.isArray(charGroup.items) ? charGroup.items : [];
    for (const item of items) {
      if (!item.id || !item.url) continue; // skip malformed entries
      sequence.push({
        storyId: item.id,
        character: charGroup.title,        // display name e.g. "Vector"
        characterLower: lower,             // URL segment e.g. "vector"
        url: item.url,
        title: item.title || '',
        image: item.image || '',
      });
    }
  }

  return sequence;
}

/**
 * Return the story that follows currentStoryId in the sequence.
 * Loops back to the beginning when currentStoryId is the last item.
 * Falls back to sequence[0] when the id is not found.
 */
export function getNextStory(currentStoryId, sequence) {
  if (!sequence || sequence.length === 0) return null;
  const idx = sequence.findIndex((s) => s.storyId === currentStoryId);
  if (idx === -1 || idx === sequence.length - 1) return sequence[0];
  return sequence[idx + 1];
}

/**
 * Return the 0-based position of storyId in the sequence.
 * @returns {{ index: number, total: number }}
 */
export function getStoryPosition(storyId, sequence) {
  if (!sequence || sequence.length === 0) return { index: 0, total: 0 };
  const idx = sequence.findIndex((s) => s.storyId === storyId);
  return { index: idx === -1 ? 0 : idx, total: sequence.length };
}
